import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import {
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '~/components/isolated/Carousel';
import type { ProductCardVariant } from '~/components/ProductCardV3';
import ProductCardM from '~/components/ProductCardV3/variants/ProductCardM/ProductCardM';
import ProductCardS from '~/components/ProductCardV3/variants/ProductCardS/ProductCardS';
import { ProductListAnalyticsProvider } from '~/components/ProductListAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { recommendationsProductList } from '~/services/analytics/productLists';
import type { RecommendationCollection } from '~/stores/RecommendationsStore';

import styles from './RecommendationSlider.module.scss';
import RecommendationSliderSkeleton from './RecommendationSliderSkeleton';

interface RecommendationSliderProps {
  collection: RecommendationCollection;
  hideBadges?: boolean;
  size?: Extract<ProductCardVariant, 'm' | 's'>;
}

const RecommendationSlider = ({
  collection,
  hideBadges = false,
  size = 's',
}: RecommendationSliderProps) => {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();

  const ProductCardComponent = size === 'm' ? ProductCardM : ProductCardS;

  const items = collection.products.map((product, index) => ({
    id: product.id,
    node: (
      <ProductCardComponent
        product={product}
        index={index}
        hideBadges={hideBadges}
      />
    ),
  }));

  const skeletonComponent = <RecommendationSliderSkeleton />;

  if (items.length === 0) {
    return <div className={styles.container}>{skeletonComponent}</div>;
  }

  const commonProps = {
    title: t(collection.nameKey),
    items,
  };

  return (
    <ProductListAnalyticsProvider
      list={recommendationsProductList(collection.id, t(collection.nameKey))}
    >
      <div className={styles.container}>
        {isMobile ? (
          <ProductCarouselMobile
            {...commonProps}
            itemClassName={styles.item}
            showViewAllOnMobile
          />
        ) : (
          <ProductCarouselDesktop
            {...commonProps}
            slideClassName={classes([styles.slide, styles[`slide_${size}`]])}
            contentClassName={styles.carouselContent}
            navigationVariant="header"
          />
        )}
      </div>
    </ProductListAnalyticsProvider>
  );
};

export default observer(RecommendationSlider);
